import { GoldRefillModalOptions } from 'redux/mode/reducers';
import Modal from '..';
import { setChatQuality, showGoldRefillModal } from 'redux/mode/actions';
import { useDispatch, useSelector } from 'react-redux';
import { Loader } from 'components/Widget/Loader';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import forge from 'apis/forge';
import { useNavigate } from 'react-router';
import { profile } from 'routes';
import { Quality } from 'dragonmouse-shared/interfaces';
import { trackEconomyAction } from 'analytics';

interface GoldRefillModalProps {
	options: GoldRefillModalOptions;
}

const GoldRefillModal = ({ options }: GoldRefillModalProps) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const [isLoading, setIsLoading] = useState(false);

	//------------------------------------------------------------------------------------------------
	// HOOKS
	//------------------------------------------------------------------------------------------------
	// Track when modal is shown
	useEffect(() => {
		trackEconomyAction('no_currency_modal_shown');
	}, []); // Only track once when modal is shown

	// I want "Escape" to close the modal if not loading
	// But the Dialog is built to trap the "Escape" key, and the only way I can latch onto it in the
	// underlying model is to add an onCancel which then shows a cancel button (gross).
	// So, instead, I hijack the "Escape" key before the Dialog can see it
	useEffect(() => {
		const handleEscape = (e: KeyboardEvent) => {
			if (e.key === 'Escape' && !isLoading) {
				e.stopPropagation();
				e.preventDefault();
				dispatch(showGoldRefillModal(null));
			}
		};

		window.addEventListener('keydown', handleEscape, true);
		return () => {
			window.removeEventListener('keydown', handleEscape, true);
		};
	}, [dispatch, isLoading]);

	//------------------------------------------------------------------------------------------------
	// HANDLERS
	//------------------------------------------------------------------------------------------------
	const onKeyPress = (e: React.KeyboardEvent<HTMLElement>) => {
		if (e.key === 'Enter' && !e.shiftKey && !isLoading) {
			e.preventDefault();
		}
	};

	const onRefill = async () => {
		if (isLoading) return;
		setIsLoading(true);
		try {
			await forge.bonusRefill({});
			trackEconomyAction('refill_claimed');
			dispatch(setChatQuality(Quality.GOLD));
			toast.success('Gold refilled successfully!');
			dispatch(showGoldRefillModal(null));
		} catch (error) {
			toast.error('Failed to refill gold');
			// Error handling via alertOnError wrapper
			setIsLoading(false);
		}
	};

	const onUpgrade = () => {
		if (isLoading) return;
		trackEconomyAction('upgrade_clicked');
		dispatch(showGoldRefillModal(null));
		navigate(profile());
	};

	const onClickOutside = (e: { preventDefault: () => void }) => {
		if (isLoading) {
			e.preventDefault();
			return;
		}
		e.preventDefault();
		dispatch(showGoldRefillModal(null));
	};

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------
	const description = options.refillsAvailable
		? 'You have a limited number of gold refills available. Claim one now, or upgrade for extended access.'
		: 'You have no more gold refills available. Upgrade for extended access.';
	return (
		<div onKeyDownCapture={onKeyPress}>
			<Modal
				disabled={isLoading}
				altActionLabel={
					isLoading ? (
						<div
							style={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								gap: '8px',
								width: '100%',
							}}
						>
							<Loader width={16} height={16} />
							<span>Claiming...</span>
						</div>
					) : (
						'Claim Refill'
					)
				}
				submitLabel="Upgrade"
				title={options.title || "You've used all your gold"}
				description={description}
				onAltAction={isLoading || !options.refillsAvailable ? undefined : onRefill}
				onSubmit={onUpgrade}
				onClickOutside={onClickOutside}
			/>
		</div>
	);
};

export default GoldRefillModal;
